import styled from '@emotion/styled';
import React from 'react';

import type { LensButtonProps } from '../../types';
import {
  getColorValue,
  getFocusRing,
  getFontWeight,
  getTextSize,
  u,
} from '../../utilities';

import Container from '../container/container';
import Icon from '../icon/icon';

const sizeStyles = {
  small: {
    textSize: 'small',
    iconSize: 2.25,
    height: u(3),
    xSpace: u(1),
    radius: 'var(--lns-radius-100)',
  },
  medium: {
    textSize: 'medium',
    iconSize: 3,
    height: u(4),
    xSpace: u(1.5),
    radius: 'var(--lns-radius-150)',
  },
  large: {
    textSize: 'large',
    iconSize: 4,
    height: u(6),
    xSpace: u(3),
    radius: 'var(--lns-radius-200)',
  },
};

const TextButtonWrapper = styled.button<TextButtonWrapperProps>`
  background-color: ${props =>
    props.isActive ? getColorValue('backgroundActive') : 'transparent'};
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  font: inherit;
  text-decoration: none;
  border: none;
  appearance: none;
  height: ${props => sizeStyles[props.size].height};
  cursor: pointer;
  transition: 0.6s background-color;
  color: ${props => getColorValue(props.color || 'body')};
  ${getFontWeight('bold')};
  border-radius: ${props => sizeStyles[props.size].radius};
  ${props => getTextSize(sizeStyles[props.size].textSize)};
  padding: 0 ${props => sizeStyles[props.size].xSpace};
  ${props =>
    props.offsetSide &&
    `margin-${props.offsetSide}: calc(-1 * ${sizeStyles[props.size].xSpace})`};

  &:focus,
  &:focus-visible {
    outline: 1px solid transparent;
  }

  &:focus-visible {
    ${getFocusRing()};
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &:hover {
    transition: 0.3s background-color;
    background-color: ${props =>
      getColorValue(props.isActive ? 'backgroundActive' : 'backgroundHover')};
  }

  &:active {
    transition: 0s background-color;
    background-color: ${getColorValue('backgroundActive')};
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    pointer-events: none;
  }
`;

type AllProps = TextButtonProps &
  React.ComponentProps<typeof TextButtonWrapper> &
  Omit<
    LensButtonProps,
    keyof TextButtonProps & React.ComponentProps<typeof TextButtonWrapper>
  >;

const TextButton = React.forwardRef(
  (
    {
      onClick,
      size = 'medium',
      children,
      icon,
      iconPosition = 'left',
      isActive,
      isDisabled,
      htmlTag,
      offsetSide,
      ...props
    }: AllProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const iconComponent = (
      <Container
        paddingLeft={iconPosition === 'right' && 'small'}
        paddingRight={iconPosition === 'left' && 'small'}
        htmlTag="span"
      >
        <Icon
          icon={icon}
          size={sizeStyles[size].iconSize}
          color={isDisabled ? 'disabledColor' : undefined}
        />
      </Container>
    );

    return (
      <TextButtonWrapper
        onClick={onClick}
        size={size}
        icon={icon}
        iconPosition={iconPosition}
        disabled={isDisabled}
        isActive={isActive}
        as={htmlTag}
        offsetSide={offsetSide}
        ref={ref}
        {...props}
      >
        {icon && iconPosition === 'left' && iconComponent}
        {children}
        {icon && iconPosition === 'right' && iconComponent}
      </TextButtonWrapper>
    );
  }
);

TextButton.displayName = 'TextButton';

type TextButtonProps = {
  onClick?: React.ReactEventHandler;
  isDisabled?: boolean;
  isActive?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  htmlTag?: 'button' | 'a';
  offsetSide?: 'left' | 'right';
  href?: string;
  target?: string;
  type?: string;
};

type TextButtonWrapperProps = {
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  as?: 'button' | 'a';
  offsetSide?: 'left' | 'right';
  isActive?: boolean;
};

export default TextButton;
